import React, { useState, useEffect, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Quote,
  Sparkles,
  ArrowUpRight,
  Pause,
  Play,
} from "lucide-react";
import { TESTIMONIALS } from "../mock";

const AUTO_MS = 9000;

const Testimonials = () => {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(Date.now());

  const t = TESTIMONIALS[idx];

  const next = () => {
    setIdx((i) => (i + 1) % TESTIMONIALS.length);
    startRef.current = Date.now();
    setProgress(0);
  };
  const prev = () => {
    setIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
    startRef.current = Date.now();
    setProgress(0);
  };
  const goTo = (i) => {
    setIdx(i);
    startRef.current = Date.now();
    setProgress(0);
  };

  useEffect(() => {
    if (!playing) return;
    startRef.current = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.min(100, (elapsed / AUTO_MS) * 100);
      setProgress(pct);
      if (elapsed >= AUTO_MS) {
        next();
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line
  }, [idx, playing]);

  return (
    <section className="relative bg-gradient-to-b from-[#fbf9f4] via-[#f4ecd8]/40 to-[#fbf9f4] py-24 md:py-32 overflow-hidden">
      {/* Decorative background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#eaf1e6] blur-3xl opacity-60" />
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-[#f4ecd8] blur-3xl opacity-70" />
      </div>

      <div className="relative max-w-[1300px] mx-auto px-6 md:px-10">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#c4a257]" />
            <span className="text-[#c4a257] text-xs tracking-[0.3em] uppercase font-medium">
              Patient Stories
            </span>
            <span className="h-px w-10 bg-[#c4a257]" />
          </div>
          <h2 className="font-serif text-[38px] md:text-[54px] leading-tight text-[#1b5e3a]">
            Journeys of <span className="italic text-[#2c7a4a]">Healing</span>
          </h2>
          <p className="text-[#5a6a5d] mt-5 max-w-2xl mx-auto">
            Real people. Real results. These are the lives that nature — with
            gentle guidance — helped restore.
          </p>
        </div>

        {/* Slide */}
        <div
          key={idx}
          className="relative grid lg:grid-cols-12 gap-6 md:gap-10 bg-white/85 backdrop-blur border border-[#efe7d5] rounded-3xl p-5 md:p-8 shadow-[0_30px_80px_rgba(27,94,58,0.12)] animate-[fadeInUp_0.6s_ease-out]"
        >
          {/* Slide image (as-is, letterboxed with soft backdrop) */}
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden h-[380px] md:h-[520px] bg-gradient-to-br from-[#eaf1e6] via-[#fbf9f4] to-[#f4ecd8] flex items-center justify-center p-2">
              <img
                src={t.image}
                alt={`${t.name} - patient story`}
                className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-[0_10px_30px_rgba(27,94,58,0.15)]"
              />

              {/* Age badge */}
              <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-full px-4 py-1.5 shadow-md z-10">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#6b7568]">
                  Patient
                </p>
                <p className="font-serif text-[#1b5e3a] text-lg leading-tight">
                  {t.name} · {t.age}
                </p>
              </div>

              {/* Accent orb */}
              <div
                className="absolute -bottom-6 -right-6 w-28 h-28 rounded-full opacity-25 blur-2xl pointer-events-none"
                style={{ background: t.accent }}
              />

              {/* Sparkle */}
              <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur-md flex items-center justify-center border border-[#efe7d5] shadow-sm z-10">
                <Sparkles className="w-4 h-4 text-[#c4a257]" />
              </div>
            </div>

            {/* Decorative marks */}
            <div className="hidden md:block absolute -top-3 -left-3 w-10 h-10 border-t-2 border-l-2 border-[#c4a257] rounded-tl-2xl" />
            <div className="hidden md:block absolute -bottom-3 -right-3 w-10 h-10 border-b-2 border-r-2 border-[#c4a257] rounded-br-2xl" />
          </div>

          {/* Story */}
          <div className="lg:col-span-7 flex flex-col">
            <div className="flex items-start gap-3">
              <Quote
                className="w-9 h-9 shrink-0 mt-1"
                style={{ color: t.accent }}
                strokeWidth={1.2}
              />
              <h3 className="font-serif text-[24px] md:text-[32px] leading-[1.15] text-[#1b5e3a]">
                {t.headline}
              </h3>
            </div>

            {/* Condition chips */}
            <div className="flex flex-wrap gap-2 mt-5">
              {t.conditions.map((c) => (
                <span
                  key={c}
                  className="text-[11px] uppercase tracking-[0.18em] font-medium px-3 py-1.5 rounded-full bg-[#fbf9f4] border border-[#efe7d5] text-[#3a4a3d]"
                >
                  {c}
                </span>
              ))}
            </div>

            {/* Quote */}
            <p className="mt-6 font-serif italic text-[18px] md:text-[19px] leading-[1.7] text-[#2c342d]">
              “{t.quote}”
            </p>

            <p className="mt-4 text-xs uppercase tracking-[0.28em] text-[#c4a257] font-medium">
              — {t.author}
            </p>

            {/* Stats */}
            <div className="mt-7 grid grid-cols-3 gap-3 md:gap-4">
              {t.stats.map((s) => (
                <div
                  key={s.label}
                  className="relative rounded-xl bg-[#fbf9f4] border border-[#efe7d5] p-3 md:p-4"
                >
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#6b7568] leading-tight">
                    {s.label}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[#8a4a3d] text-sm md:text-base font-medium line-through opacity-70">
                      {s.before}
                    </span>
                    <ArrowUpRight
                      className="w-3.5 h-3.5"
                      style={{ color: t.accent }}
                    />
                  </div>
                  <p
                    className="mt-1 font-serif text-lg md:text-xl leading-tight"
                    style={{ color: t.accent }}
                  >
                    {s.after}
                  </p>
                </div>
              ))}
            </div>

            {/* Controls */}
            <div className="mt-auto pt-8 flex items-center gap-4">
              <button
                onClick={prev}
                aria-label="Previous story"
                className="w-11 h-11 rounded-full border border-[#efe7d5] flex items-center justify-center text-[#1b5e3a] hover:bg-[#1b5e3a] hover:text-white hover:border-[#1b5e3a] transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={next}
                aria-label="Next story"
                className="w-11 h-11 rounded-full border border-[#efe7d5] flex items-center justify-center text-[#1b5e3a] hover:bg-[#1b5e3a] hover:text-white hover:border-[#1b5e3a] transition-all"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPlaying((p) => !p)}
                aria-label={playing ? "Pause" : "Play"}
                className="w-11 h-11 rounded-full border border-[#efe7d5] flex items-center justify-center text-[#1b5e3a] hover:bg-[#c4a257] hover:text-white hover:border-[#c4a257] transition-all"
              >
                {playing ? (
                  <Pause className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
              </button>

              <div className="flex-1 h-1 bg-[#efe7d5] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-[width] duration-100 ease-linear"
                  style={{
                    width: `${progress}%`,
                    background: t.accent,
                  }}
                />
              </div>
              <p className="text-xs text-[#6b7568] tabular-nums font-medium min-w-[46px] text-right">
                {String(idx + 1).padStart(2, "0")} /{" "}
                {String(TESTIMONIALS.length).padStart(2, "0")}
              </p>
            </div>
          </div>
        </div>

        {/* Thumbnails */}
        <div className="mt-10 grid grid-cols-3 sm:grid-cols-5 md:grid-cols-5 lg:grid-cols-10 gap-2 md:gap-3">
          {TESTIMONIALS.map((s, i) => (
            <button
              key={s.name}
              onClick={() => goTo(i)}
              className={`group relative rounded-xl overflow-hidden h-16 md:h-20 border transition-all duration-300 bg-gradient-to-br from-[#eaf1e6] to-[#f4ecd8] flex items-center justify-center ${
                i === idx
                  ? "border-[#c4a257] shadow-[0_10px_24px_rgba(196,162,87,0.35)] scale-[1.04]"
                  : "border-transparent opacity-75 hover:opacity-100"
              }`}
              aria-label={`View ${s.name}'s story`}
            >
              <img
                src={s.image}
                alt={s.name}
                className="max-w-full max-h-full object-contain"
              />
              <div
                className={`absolute inset-0 pointer-events-none ${
                  i === idx
                    ? "bg-gradient-to-t from-[#1b5e3a]/50 via-transparent to-transparent"
                    : "bg-[#1b5e3a]/25 group-hover:bg-transparent"
                } transition-colors`}
              />
              <div className="absolute bottom-1.5 left-2 right-2 text-white text-left pointer-events-none">
                <p className="font-serif text-xs md:text-sm leading-tight truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
                  {s.name}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
